import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { Router, NavigationStart } from '@angular/router';

import { VideoScannerService } from '../../../../services/video-scanner/video-scanner.service';
import { FaceCompareService } from '../../../../services/face-compare/face-compare.service';
import { FaceDbService } from '../../../../services/face-db/face-db.service';

@Component({
  selector: 'app-frame-generator',
  imports: [CommonModule],
  templateUrl: './frame-generator.component.html',
  styleUrl: './frame-generator.component.css',
})
export class FrameGeneratorComponent implements OnInit, OnDestroy {
  @Input() videoData!: any;
  @Output() changeVideo = new EventEmitter<void>();
  @Output() showResult = new EventEmitter<void>();
  @Output() framesExtracted = new EventEmitter<any[]>();

  @ViewChild('videoPlayer') videoPlayer!: ElementRef<HTMLVideoElement>;

  interval: number = 1.1;
  confidence: number = 80;
  isScrapping = false;
  isPlaying = false;
  currentTime = 0;
  duration = 0;
  progress = 0;
  isScanning = false;
  scanProgress = 0;

  volume = 80;
  isMuted = false;

  isFullscreen = false;
  extractedFaceImages: any[] = [];

  extractionProgress: any = {
    currentFrame: 0,
    totalFrames: 0,
    progress: 0,
    isComplete: false,
  };

  scrapProgress: any = {
    currentFrame: 0,
    totalFrames: 0,
    progress: 0,
    isComplete: false,
  };

  private routerEventsSubscription: any;
  private currentVideoScannerId: string | null = null;
  private sessionFramesScanned = 0;
  private sessionFacesFound = 0;
  private appendFailureToasted = false;
  private sessionCompleting = false;
  /** When true, no new frame appends are started (complete/cancel in progress). */
  private sessionFinalizing = false;
  /** In-flight append promises; must settle before complete. */
  private pendingAppendPromises: Promise<void>[] = [];

  constructor(
    private videoScannerService: VideoScannerService,
    private faceCompareService: FaceCompareService,
    private router: Router,
    private facedb: FaceDbService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.addFullscreenListeners();
    this.subscribeToFrameExtraction();
    // stop extraction immediately when navigating away
    this.routerEventsSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.videoScannerService.stopFrameExtraction();
        void this.bestEffortCompleteSession();
        this.resetControlsToDefaults();
      }
    });
  }

  ngOnDestroy(): void {
    this.removeFullscreenListeners();
    this.videoScannerService.stopFrameExtraction();
    void this.bestEffortCompleteSession();
    if (this.routerEventsSubscription) {
      this.routerEventsSubscription.unsubscribe();
    }
  }

  private subscribeToFrameExtraction(): void {
    this.videoScannerService.frameExtractionProgress$.subscribe(
      (progress: any) => {
        this.extractionProgress = progress;
        this.scanProgress = progress.progress;
        this.isScanning = progress.totalFrames > 0 && !progress.isComplete;
      },
    );

    this.videoScannerService.frameExtractionComplete$.subscribe(
      (frames: any[]) => {
        this.framesExtracted.emit(frames);
        this.processExtractedFrames(frames);
        this.resetVideoAfterExtraction();
      },
    );
  }

  private addFullscreenListeners(): void {
    document.addEventListener(
      'fullscreenchange',
      this.onFullscreenChange.bind(this),
    );
    document.addEventListener(
      'webkitfullscreenchange',
      this.onFullscreenChange.bind(this),
    );
    document.addEventListener(
      'mozfullscreenchange',
      this.onFullscreenChange.bind(this),
    );
    document.addEventListener(
      'MSFullscreenChange',
      this.onFullscreenChange.bind(this),
    );
  }

  private removeFullscreenListeners(): void {
    document.removeEventListener(
      'fullscreenchange',
      this.onFullscreenChange.bind(this),
    );
    document.removeEventListener(
      'webkitfullscreenchange',
      this.onFullscreenChange.bind(this),
    );
    document.removeEventListener(
      'mozfullscreenchange',
      this.onFullscreenChange.bind(this),
    );
    document.removeEventListener(
      'MSFullscreenChange',
      this.onFullscreenChange.bind(this),
    );
  }

  private onFullscreenChange(): void {
    const isCurrentlyFullscreen = !!(
      document.fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).mozFullScreenElement ||
      (document as any).msFullscreenElement
    );

    this.isFullscreen = isCurrentlyFullscreen;
  }

  onPlayPause(): void {
    if (this.videoPlayer) {
      if (this.isPlaying) {
        this.videoPlayer.nativeElement.pause();
        this.isPlaying = false;
      } else {
        this.videoPlayer.nativeElement.play();
        this.isPlaying = true;
      }
    }
  }

  private resetVideoAfterExtraction(): void {
    if (this.videoPlayer) {
      this.videoPlayer.nativeElement.pause();
      this.videoPlayer.nativeElement.currentTime = 0;
      this.isPlaying = false;
      this.currentTime = 0;
      this.progress = 0;
    }
  }

  onTimeUpdate(): void {
    if (this.videoPlayer) {
      this.currentTime = this.videoPlayer.nativeElement.currentTime;
      this.duration = this.videoPlayer.nativeElement.duration;
      this.progress = (this.currentTime / this.duration) * 100;

      if (this.videoPlayer.nativeElement.volume !== this.volume / 100) {
        this.videoPlayer.nativeElement.volume = this.volume / 100;
      }
      if (this.videoPlayer.nativeElement.muted !== this.isMuted) {
        this.videoPlayer.nativeElement.muted = this.isMuted;
      }
    }
  }

  onVideoEnded(): void {
    this.resetVideoAfterExtraction();
  }

  onSeek(event: any): void {
    if (this.videoPlayer && this.duration > 0) {
      const progressValue = parseFloat(event.target.value);
      const newTime = (progressValue / 100) * this.duration;
      const clampedTime = Math.max(0, Math.min(newTime, this.duration));
      this.videoPlayer.nativeElement.currentTime = clampedTime;
    }
  }

  onIntervalChange(event: any): void {
    this.interval = parseFloat(event.target.value);
  }

  onConfidenceChange(event: any): void {
    this.confidence = parseFloat(event.target.value);
  }

  onVolumeChange(event: any): void {
    if (this.videoPlayer) {
      const newVolume = parseFloat(event.target.value);
      const clampedVolume = Math.max(0, Math.min(newVolume, 100));
      this.volume = clampedVolume;
      this.videoPlayer.nativeElement.volume = clampedVolume / 100;

      if (clampedVolume === 0) {
        this.videoPlayer.nativeElement.muted = true;
        this.isMuted = true;
      } else {
        this.videoPlayer.nativeElement.muted = false;
        this.isMuted = false;
      }
    }
  }

  onVolumeMute(): void {
    if (this.videoPlayer) {
      if (this.isMuted) {
        this.videoPlayer.nativeElement.muted = false;
        this.videoPlayer.nativeElement.volume = this.volume / 100;
        this.isMuted = false;
      } else {
        this.videoPlayer.nativeElement.muted = true;
        this.isMuted = true;
      }
    }
  }

  onChangeVideoClicked(): void {
    this.videoScannerService.stopFrameExtraction();
    void this.bestEffortCompleteSession();
    this.changeVideo.emit();
  }

  private resetControlsToDefaults(): void {
    // Reset sliders and controls to initial defaults
    this.interval = 1;
    this.confidence = 80;
    this.volume = 80;
    this.isMuted = false;

    // Reset playback/progress
    this.isPlaying = false;
    this.progress = 0;
    this.currentTime = 0;
    this.isFullscreen = false;

    // Reset scanning state UI
    this.isScanning = false;
    this.scanProgress = 0;
    this.extractionProgress = {
      currentFrame: 0,
      totalFrames: 0,
      progress: 0,
      isComplete: false,
    };

    this.isScrapping = false;
    this.scrapProgress = {
      currentFrame: 0,
      totalFrames: 0,
      progress: 0,
      isComplete: false,
    };

    // Reset video element if present
    if (this.videoPlayer) {
      try {
        this.videoPlayer.nativeElement.pause();
        this.videoPlayer.nativeElement.currentTime = 0;
        this.videoPlayer.nativeElement.volume = this.volume / 100;
        this.videoPlayer.nativeElement.muted = this.isMuted;
      } catch {}
    }
  }

  onFullscreenToggle(): void {
    if (this.videoPlayer) {
      if (!this.isFullscreen) {
        this.enterFullscreen();
      } else {
        this.exitFullscreen();
      }
    }
  }

  private enterFullscreen(): void {
    const video = this.videoPlayer.nativeElement;

    if (video.requestFullscreen) {
      video.requestFullscreen();
    } else if ((video as any).webkitRequestFullscreen) {
      (video as any).webkitRequestFullscreen();
    } else if ((video as any).msRequestFullscreen) {
      (video as any).msRequestFullscreen();
    } else if ((video as any).mozRequestFullScreen) {
      (video as any).mozRequestFullScreen();
    }

    this.isFullscreen = true;
  }

  private exitFullscreen(): void {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if ((document as any).webkitExitFullscreen) {
      (document as any).webkitExitFullscreen();
    } else if ((document as any).msExitFullscreen) {
      (document as any).msExitFullscreen();
    } else if ((document as any).mozCancelFullScreen) {
      (document as any).mozCancelFullScreen();
    }

    this.isFullscreen = false;
  }

  async startScanning(): Promise<void> {
    if (!this.videoPlayer || !this.duration) {
      console.warn('[FrameGenerator] startScanning: video not ready');
      return;
    }

    const sessionCreated = await this.createScanSession();
    if (!sessionCreated) {
      return;
    }

    try {
      await this.videoScannerService.startFrameExtraction(
        this.videoPlayer.nativeElement,
        this.interval,
        this.duration,
      );
    } catch (error) {
      console.error('[FrameGenerator] startScanning error', error);
      await this.bestEffortCompleteSession();
    }
  }

  private async createScanSession(): Promise<boolean> {
    this.currentVideoScannerId = null;
    this.sessionFramesScanned = 0;
    this.sessionFacesFound = 0;
    this.appendFailureToasted = false;
    this.sessionCompleting = false;
    this.sessionFinalizing = false;
    this.pendingAppendPromises = [];

    const tracking = this.retrieveTrackingPayload();
    const payload = {
      video_name: this.videoData?.name || null,
      frame_interval: this.interval,
      ...tracking,
    };

    try {
      const response = await firstValueFrom(
        this.videoScannerService.createVideoScannerSession(payload),
      );
      const sessionId = response?.data?.video_scanner_id;
      if (!sessionId) {
        this.toastr.error('Unable to start video scan session. Please try again.');
        return false;
      }
      this.currentVideoScannerId = sessionId;
      return true;
    } catch (error) {
      console.error('[FrameGenerator] create session failed', error);
      this.toastr.error('Unable to start video scan session. Please try again.');
      return false;
    }
  }

  private retrieveTrackingPayload(): {
    ip_address?: string;
    location?: string;
    device?: string;
  } {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : null;
    const ip = userPortal?.ipDetails?.ip || '';
    const location = userPortal?.ipDetails?.city || '';
    const tracking: {
      ip_address?: string;
      location?: string;
      device?: string;
    } = { device: 'Chrome' };
    if (ip) tracking.ip_address = ip;
    if (location) tracking.location = location;
    return tracking;
  }

  private async processExtractedFrames(frames: any[]): Promise<void> {
    this.isScrapping = true;
    this.scrapProgress.totalFrames = frames.length;
    this.facedb.clearFaces();
    let currentFrame = 1;

    try {
      for (const frame of frames) {
        this.scrapProgress.currentFrame = currentFrame;
        this.scrapProgress.progress = Math.min(
          ((currentFrame + 1) / frames.length) * 100,
          100,
        );

        try {
          await this.processFrameForFaceDetection(
            frame.blob,
            frame.index,
            frame.timestamp,
            frame.dataUrl,
          );
        } catch (err) {
          console.error('[FrameGenerator] frame processing failed', {
            frameIndex: frame.index,
            err,
          });
        }

        this.sessionFramesScanned += 1;
        currentFrame++;
      }
    } finally {
      this.scrapProgress.isComplete = true;
      await this.bestEffortCompleteSession();
      setTimeout(() => {
        let resultTitle = document.querySelector('#resultTitle');
        if (resultTitle) {
          resultTitle.scrollIntoView();
        }
      }, 1000);
      this.showResult.emit();
    }
  }

  private processFrameForFaceDetection(
    frameBlob: Blob,
    frameIndex: number,
    timestamp: any,
    frameDataUrl?: string,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      // Create blob URL from the blob
      const blobUrl = URL.createObjectURL(frameBlob);

      // Convert blob to base64 for the face detection API
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64Data = (reader.result as string) || frameDataUrl || '';

        this.faceCompareService.getFacesFromImage(base64Data).subscribe({
          next: (data) => {
            const detectPayload = data?.data || data;
            const detectedFaces = detectPayload?.detected_faces || [];
            if (detectedFaces.length > 0) {
              this.addNewFace({
                image: blobUrl, // Store blob URL instead of base64
                data: detectPayload,
                timestamp: timestamp,
              });
              this.sessionFacesFound += detectedFaces.length;
              this.enqueueAppendFrame(
                base64Data,
                detectPayload,
                frameIndex,
                timestamp,
              );
            }
            resolve();
          },
          error: (err) => {
            console.error('[FrameGenerator] face detection failed', {
              frameIndex,
              timestamp,
              err,
            });
            // Clean up blob URL on error
            URL.revokeObjectURL(blobUrl);
            reject(err);
          },
        });
      };

      reader.onerror = () => {
        console.error('[FrameGenerator] Failed to read blob', {
          frameIndex,
          timestamp,
        });
        URL.revokeObjectURL(blobUrl);
        reject(new Error('Failed to read blob'));
      };

      reader.readAsDataURL(frameBlob);
    });
  }

  /**
   * Fire append without blocking face-detection loop, but track the promise
   * so complete waits for all appends (avoids already_done / missing DB rows).
   */
  private enqueueAppendFrame(
    imageDataUri: string,
    detectPayload: any,
    frameIndex: number,
    timestamp: any,
  ): void {
    if (!this.currentVideoScannerId || this.sessionFinalizing) {
      return;
    }

    const appendPromise = this.appendFrameToSession(
      imageDataUri,
      detectPayload,
      frameIndex,
      timestamp,
    );
    this.pendingAppendPromises.push(appendPromise);
  }

  private async appendFrameToSession(
    imageDataUri: string,
    detectPayload: any,
    frameIndex: number,
    timestamp: any,
  ): Promise<void> {
    const sessionId = this.currentVideoScannerId;
    if (!sessionId) {
      return;
    }

    try {
      await firstValueFrom(
        this.videoScannerService.appendVideoScannerFrame(sessionId, {
          image: imageDataUri,
          detect_payload: detectPayload,
          frame_index: frameIndex,
          timestamp_sec: Number(timestamp) || 0,
          image_file_name: `frame_${frameIndex}.jpg`,
          ...this.retrieveTrackingPayload(),
        }),
      );
    } catch (error) {
      console.error('[FrameGenerator] append frame failed', {
        frameIndex,
        error,
      });
      if (!this.appendFailureToasted) {
        this.appendFailureToasted = true;
        this.toastr.warning(
          'Some frames could not be saved to history. Scan will continue.',
        );
      }
    }
  }

  /** Drain in-flight appends, including any enqueued while a batch was settling. */
  private async flushPendingAppends(): Promise<void> {
    while (this.pendingAppendPromises.length > 0) {
      const batch = this.pendingAppendPromises.splice(0);
      await Promise.allSettled(batch);
    }
  }

  private async bestEffortCompleteSession(): Promise<void> {
    if (!this.currentVideoScannerId || this.sessionCompleting) {
      return;
    }

    this.sessionCompleting = true;
    this.sessionFinalizing = true;
    const sessionId = this.currentVideoScannerId;

    try {
      // Strict ordering: create → appends → complete. Never mark done while
      // appends are still in flight (late appends get already_done and are dropped).
      await this.flushPendingAppends();

      await firstValueFrom(
        this.videoScannerService.completeVideoScannerSession(sessionId, {
          frames_scanned: this.sessionFramesScanned,
          faces_found: this.sessionFacesFound,
          ...this.retrieveTrackingPayload(),
        }),
      );
    } catch (error) {
      console.error('[FrameGenerator] complete session failed', error);
    } finally {
      this.currentVideoScannerId = null;
      this.pendingAppendPromises = [];
      this.sessionCompleting = false;
    }
  }

  formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  formatFileSize(bytes: number): string {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  }

  async addNewFace(newFace: { image: string; data: any; timestamp: any }) {
    const existingFaces = await this.facedb.getFaces();
    existingFaces.push(newFace);
    await this.facedb.persistFaces(existingFaces);
  }
}
